import { createAsyncThunk } from "@reduxjs/toolkit";
import { agent, encodeGetParams } from "../../api";
import { apiUrlV1 } from "../../config/config";
import { ResponseObjectList } from "../../entities/global";
import {
  AdjustWalletPayload,
  CohortHistoryItem,
  IntegrationEventsResponse,
  UserActivitySummary,
  UserAggregates,
  UserCard,
  UserDeliveryState,
  UserEventItem,
  UserListItem,
  SetGameProgressPayload,
  UserGameItem,
  WalletTransaction,
} from "../../entities/user";
import { addNotification } from "../Notification/notificateStore";

const BASE = `${apiUrlV1}/organization/users`;

export const getUsers = createAsyncThunk(
  "users/get-users",
  async (params?: Record<string, string | number>): Promise<ResponseObjectList<UserListItem[]>> => {
    const query = encodeGetParams({ ...params });
    const req = await agent.get(`${BASE}${query}`);
    if (req.ok) {
      return (await req.json()) as ResponseObjectList<UserListItem[]>;
    }
    return { data: [], limit: 50, page: 1, pages: 1, total: 0 };
  }
);

export const getUserCard = createAsyncThunk(
  "users/get-user-card",
  async (params: { customerUserId: string }): Promise<UserCard | null> => {
    const req = await agent.get(`${BASE}/${encodeURIComponent(params.customerUserId)}`);
    if (req.ok) {
      return (await req.json()) as UserCard;
    }
    return null;
  }
);

export const getUserTransactions = createAsyncThunk(
  "users/get-user-transactions",
  async (params: {
    customerUserId: string;
    query?: Record<string, string | number>;
  }): Promise<ResponseObjectList<WalletTransaction[]>> => {
    const query = encodeGetParams({ ...params.query });
    const req = await agent.get(
      `${BASE}/${encodeURIComponent(params.customerUserId)}/wallet/transactions${query}`
    );
    if (req.ok) {
      return (await req.json()) as ResponseObjectList<WalletTransaction[]>;
    }
    return { data: [], limit: 100, page: 1, pages: 1, total: 0 };
  }
);

export const getUserCohortHistory = createAsyncThunk(
  "users/get-cohort-history",
  async (params: { customerUserId: string }): Promise<CohortHistoryItem[]> => {
    const req = await agent.get(
      `${BASE}/${encodeURIComponent(params.customerUserId)}/cohorts/history`
    );
    if (req.ok) {
      return (await req.json()) as CohortHistoryItem[];
    }
    return [];
  }
);

// Per-tab event history — consumed via .unwrap() locally, no store slot.
export const getUserEvents = createAsyncThunk(
  "users/get-events",
  async (params: {
    customerUserId: string;
    query?: { events?: string; fromDate?: string; toDate?: string; page?: number; limit?: number };
  }): Promise<ResponseObjectList<UserEventItem[]>> => {
    const query = encodeGetParams({ ...params.query });
    const req = await agent.get(`${BASE}/${encodeURIComponent(params.customerUserId)}/events${query}`);
    if (req.ok) {
      return (await req.json()) as ResponseObjectList<UserEventItem[]>;
    }
    return { data: [], limit: 50, page: 1, pages: 1, total: 0 };
  }
);

// Customer real-money integration events for one player (deposit/bet/withdrawal/…) — local unwrap.
export const getUserIntegrationEvents = createAsyncThunk(
  "users/get-integration-events",
  async (params: {
    customerUserId: string;
    query?: { fromDate?: string; toDate?: string; limit?: number; page?: number };
  }): Promise<IntegrationEventsResponse | null> => {
    const query = encodeGetParams({ ...params.query });
    const req = await agent.get(`${BASE}/${encodeURIComponent(params.customerUserId)}/integration-events${query}`);
    if (req.ok) {
      return (await req.json()) as IntegrationEventsResponse;
    }
    return null;
  }
);

export const getUserActivitySummary = createAsyncThunk(
  "users/get-activity-summary",
  async (params: { customerUserId: string }): Promise<UserActivitySummary> => {
    const req = await agent.get(`${BASE}/${encodeURIComponent(params.customerUserId)}/activity-summary`);
    if (req.ok) {
      return (await req.json()) as UserActivitySummary;
    }
    return { firstInteraction: null, lastInteraction: null, firstSeen: null, lastSeen: null };
  }
);

// Card summary strip (online + wallet totals + bonus totals) — consumed via .unwrap() locally.
export const getUserAggregates = createAsyncThunk(
  "users/get-aggregates",
  async (params: { customerUserId: string }): Promise<UserAggregates | null> => {
    const req = await agent.get(`${BASE}/${encodeURIComponent(params.customerUserId)}/aggregates`);
    if (req.ok) {
      return (await req.json()) as UserAggregates;
    }
    return null;
  }
);

export const patchUserProfile = createAsyncThunk(
  "users/patch-profile",
  async (params: { customerUserId: string; data: { nickname?: string; avatarUrl?: string } }, thunksApi) => {
    const response = await agent.patch(`${BASE}/${encodeURIComponent(params.customerUserId)}`, {
      body: JSON.stringify(params.data),
    });
    if (response.ok) {
      thunksApi.dispatch(addNotification({ status: "success", text: "Profile updated" }));
      thunksApi.dispatch(getUserCard({ customerUserId: params.customerUserId }));
      return;
    }
    const error = (await response.json()) as { message: string };
    thunksApi.dispatch(addNotification({ status: "error", text: error.message }));
    throw Error(error.message);
  }
);

export const adjustWallet = createAsyncThunk(
  "users/adjust-wallet",
  async (params: { customerUserId: string; data: AdjustWalletPayload }, thunksApi) => {
    const response = await agent.patch(`${BASE}/${encodeURIComponent(params.customerUserId)}/wallet`, {
      body: JSON.stringify(params.data),
    });
    if (response.ok) {
      thunksApi.dispatch(addNotification({ status: "success", text: "Wallet updated" }));
      thunksApi.dispatch(getUserCard({ customerUserId: params.customerUserId }));
      thunksApi.dispatch(getUserTransactions({ customerUserId: params.customerUserId }));
      return;
    }
    const error = (await response.json()) as { message: string };
    thunksApi.dispatch(addNotification({ status: "error", text: error.message }));
    throw Error(error.message);
  }
);

export const addUserCohort = createAsyncThunk(
  "users/add-cohort",
  async (params: { customerUserId: string; cohortId: number }, thunksApi) => {
    const response = await agent.post(`${BASE}/${encodeURIComponent(params.customerUserId)}/cohorts`, {
      body: JSON.stringify({ cohortId: params.cohortId }),
    });
    if (response.ok) {
      thunksApi.dispatch(addNotification({ status: "success", text: "Added to cohort" }));
      thunksApi.dispatch(getUserCard({ customerUserId: params.customerUserId }));
      return;
    }
    const error = (await response.json()) as { message: string };
    thunksApi.dispatch(addNotification({ status: "error", text: error.message }));
    throw Error(error.message);
  }
);

export const removeUserCohort = createAsyncThunk(
  "users/remove-cohort",
  async (params: { customerUserId: string; cohortId: number }, thunksApi) => {
    const response = await agent.delete(
      `${BASE}/${encodeURIComponent(params.customerUserId)}/cohorts/${params.cohortId}`
    );
    if (response.ok) {
      thunksApi.dispatch(addNotification({ status: "success", text: "Removed from cohort" }));
      thunksApi.dispatch(getUserCard({ customerUserId: params.customerUserId }));
      return;
    }
    const error = (await response.json()) as { message: string };
    thunksApi.dispatch(addNotification({ status: "error", text: error.message }));
    throw Error(error.message);
  }
);

export const getUserGames = createAsyncThunk(
  "users/get-user-games",
  async (params: { customerUserId: string }): Promise<UserGameItem[]> => {
    const req = await agent.get(`${BASE}/${encodeURIComponent(params.customerUserId)}/games`);
    if (req.ok) {
      return (await req.json()) as UserGameItem[];
    }
    return [];
  }
);

export const setUserGameProgress = createAsyncThunk(
  "users/set-user-game-progress",
  async (
    params: { customerUserId: string; gameId: number; data: SetGameProgressPayload },
    thunksApi
  ) => {
    const response = await agent.patch(
      `${BASE}/${encodeURIComponent(params.customerUserId)}/games/${params.gameId}/progress`,
      { body: JSON.stringify(params.data) }
    );
    if (response.ok) {
      thunksApi.dispatch(addNotification({ status: "success", text: "Progress updated" }));
      return;
    }
    const error = (await response.json()) as { message: string };
    thunksApi.dispatch(addNotification({ status: "error", text: error.message }));
    throw Error(error.message);
  }
);

// ---------------------------------------------------------------- delivery queue (Redis-only)
// Live view of what is queued for one player and which locks hold it back. Consumed via
// .unwrap() locally — it is a "now" snapshot, so it is never cached in the store.
export const getUserDelivery = createAsyncThunk(
  "users/get-delivery",
  async (params: { customerUserId: string }): Promise<UserDeliveryState | null> => {
    const req = await agent.get(`${BASE}/${encodeURIComponent(params.customerUserId)}/delivery`);
    if (req.ok) {
      return (await req.json()) as UserDeliveryState;
    }
    return null;
  }
);

export const clearUserDeliveryQueue = createAsyncThunk(
  "users/clear-delivery-queue",
  async (params: { customerUserId: string }, thunksApi) => {
    const response = await agent.delete(`${BASE}/${encodeURIComponent(params.customerUserId)}/delivery/queue`);
    if (response.ok) {
      const result = (await response.json()) as { triggers: number; notifications: number };
      thunksApi.dispatch(
        addNotification({
          status: "success",
          text: `Queue cleared: ${result.triggers} trigger(s), ${result.notifications} notification(s)`,
        })
      );
      return result;
    }
    const error = (await response.json()) as { message: string };
    thunksApi.dispatch(addNotification({ status: "error", text: error.message }));
    throw Error(error.message);
  }
);

export const clearUserDeliveryLocks = createAsyncThunk(
  "users/clear-delivery-locks",
  async (params: { customerUserId: string }, thunksApi) => {
    const response = await agent.delete(`${BASE}/${encodeURIComponent(params.customerUserId)}/delivery/locks`);
    if (response.ok) {
      const result = (await response.json()) as { interacting: boolean; cooldowns: number };
      thunksApi.dispatch(addNotification({ status: "success", text: "Locks released" }));
      return result;
    }
    const error = (await response.json()) as { message: string };
    thunksApi.dispatch(addNotification({ status: "error", text: error.message }));
    throw Error(error.message);
  }
);
